import React from 'react';
import { useGame } from '../../context/GameContext';
import { KINGDOM_TYPES } from '../../data/world-map/kingdoms';
import './DiplomacyPanel.css';

const DiplomacyPanel = () => {
  const { worldMap, getCurrentNodeData, modifyKingdomFavor, getKingdomFavorBonuses } = useGame();

  // Get current location data
  const currentNode = getCurrentNodeData?.() || null;
  const currentBiome = currentNode?.biomeData;
  const isOnKingdom = currentBiome?.isKingdomBiome;

  // If not on kingdom, show empty state
  if (!isOnKingdom) {
    return (
      <div className="diplomacy-panel">
        <div className="dp-empty-state">
          <div className="dp-empty-icon">🏰</div>
          <h2>No Kingdom Access</h2>
          <p>Travel to a kingdom or city-state to engage in diplomacy and access their markets.</p>
          <div className="dp-empty-hint">
            💡 Discovered kingdoms appear as {Object.keys(worldMap.discoveredKingdoms || {}).length > 0 ? (
              <>special tiles on the world map ({Object.keys(worldMap.discoveredKingdoms).map(k => KINGDOM_TYPES[worldMap.discoveredKingdoms[k].kingdomId]?.icon).join(' ')})</>
            ) : (
              'special tiles on the world map'
            )}
          </div>
        </div>
      </div>
    );
  }

  // Get current kingdom data
  const kingdomId = currentNode.biome;
  const kingdomData = KINGDOM_TYPES[kingdomId];
  const discoveredKingdom = worldMap.discoveredKingdoms?.[worldMap.currentNode];
  const relationshipLevel = discoveredKingdom?.relationshipLevel || 0;

  if (!kingdomData) {
    return (
      <div className="diplomacy-panel">
        <div className="dp-empty-state">
          <div className="dp-empty-icon">❓</div>
          <h2>Unknown Kingdom</h2>
          <p>Kingdom data could not be loaded.</p>
        </div>
      </div>
    );
  }

  // Show only current kingdom
  return (
    <div className="diplomacy-panel">
      <div className="dp-current-kingdom">
        {/* Kingdom header with icon, name, type */}
        <div className="dp-kingdom-header" style={{ borderColor: kingdomData.color }}>
          <div className="dp-kingdom-icon" style={{ backgroundColor: kingdomData.color }}>
            {kingdomData.icon}
          </div>
          <div className="dp-kingdom-title">
            <h2>{kingdomData.name}</h2>
            <div className="dp-kingdom-type">
              {kingdomData.type === 'kingdom' ? '👑 Kingdom' : '🏛️ City-State'} • Tier {kingdomData.tier}
            </div>
          </div>
        </div>

        {/* Relationship level */}
        <div className="dp-relationship">
          <span className="dp-rel-label">Favor:</span>
          <span className="dp-rel-stars">
            {'⭐'.repeat(Math.min(relationshipLevel, 5))}
            {'☆'.repeat(Math.max(0, 5 - relationshipLevel))}
          </span>
        </div>

        {/* Favor Controls (Dev/Testing) */}
        <div style={{ display: 'flex', gap: '6px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
          <button
            style={{
              flex: 1,
              padding: '4px 8px',
              fontSize: '0.7rem',
              background: 'rgba(96, 128, 168, 0.3)',
              border: '1px solid rgba(96, 128, 168, 0.5)',
              borderRadius: '4px',
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
            onClick={() => modifyKingdomFavor(worldMap.currentNode, 0.1, 'Test Favor Gain')}
          >
            +0.1 ⭐
          </button>
          <button
            style={{
              flex: 1,
              padding: '4px 8px',
              fontSize: '0.7rem',
              background: 'rgba(192, 96, 96, 0.3)',
              border: '1px solid rgba(192, 96, 96, 0.5)',
              borderRadius: '4px',
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
            onClick={() => modifyKingdomFavor(worldMap.currentNode, -0.1, 'Test Favor Loss')}
          >
            -0.1 ⭐
          </button>
        </div>

        {/* Favor Bonuses Display */}
        {(() => {
          const bonuses = getKingdomFavorBonuses(kingdomId);
          if (Object.keys(bonuses).length === 0) return null;

          return (
            <div style={{
              padding: '8px 10px',
              background: 'rgba(128, 184, 88, 0.15)',
              border: '1px solid rgba(128, 184, 88, 0.3)',
              borderRadius: '6px'
            }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>
                Active Bonuses
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--success)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {bonuses.production && <div>+{(bonuses.production * 100).toFixed(0)}% Production (Specialty Resources)</div>}
                {bonuses.discount && <div>+{(bonuses.discount * 100).toFixed(0)}% Shop Discount</div>}
              </div>
            </div>
          );
        })()}

        {/* Description */}
        <div className="dp-description">
          <p>{kingdomData.description}</p>
        </div>

        {/* Specialties */}
        <div className="dp-specialties">
          <h3>Specialties</h3>
          <div className="dp-specialty-grid">
            {kingdomData.specialties.map((specialty, idx) => (
              <div key={idx} className="dp-specialty-badge">
                👑 {specialty}
              </div>
            ))}
          </div>
        </div>

        {/* Coming Soon: Market Access */}
        <div className="dp-market-hint">
          <div className="dp-hint-icon">🏪</div>
          <p><strong>Markets & Services</strong> - Coming soon! Access shops, quests, and more.</p>
        </div>
      </div>
    </div>
  );
};

export default DiplomacyPanel;
